import { AfterViewInit, Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Utilities } from '../../helper/util';

enum ErrorDisplay {
    LABEL = 'label',
    INLINE = 'inline'
}

@Component({
    template: ''
})
export abstract class InputComponent implements AfterViewInit, ControlValueAccessor {

    @Output() showHelpClick: EventEmitter<void> = new EventEmitter<void>();
    @Input() showHelp = false;
    @Input() label?: string;
    @Input() color?: string;
    @Input() fontSize?: string;
    @Input() subLabel?: string;
    @Input() helperText?: string;
    @Input() minLength = 0;
    @Input() debounce = 0;
    @Input() focus = false;
    @Input() maxLength: string | number | null = 100;
    @Input() disabled = false;
    @Input() required: string | boolean = false;
    @Input() lockIndicator = false;
    @Input() placeholder = 'Enter information...';
    @Input() lockTitle = 'This field is locked and cannot be edited';
    @Input() errorMessage = 'This field is required';
    @Input() errorDisplay: string = ErrorDisplay.LABEL;
    @Output() checkingValidity: EventEmitter<InputComponent> = new EventEmitter<InputComponent>();

    // this is used for auto saving fields on blur, or if we need to calculate something on blur
    @Output() blurredAndValid: EventEmitter<void> = new EventEmitter<void>();
    @Output() blurred: EventEmitter<void> = new EventEmitter<void>();

    // fires when event changed
    @Output() changed: EventEmitter<any> = new EventEmitter();

    // boolean value to determine if the input value is valid
    isValid = true;
    id: string = this.getRandomId();

    hideLabel: boolean = true;
    origPlaceholder?: string;

    private innerValue?: string;

    constructor() {
    }

    // functions for control value accessor
    get value(): any {
        return this.innerValue;
    }

    set value(value: any) {
        // IF the value is longer than the max length, we need to truncate it
        if (this.maxLength && value && value.length > this.maxLength) {
            value = value.substring(0, this.maxLength);
            // find the element and set it's inner value to the truncated value
            const element: any = document.getElementById(this.id);
            if (element) {
                element.value = value;
            }
        }

        if (value !== this.innerValue) {
            this.innerValue = value;
            this.onChange(value);
            this.onTouch(value);
            this.updateValue();
            this.changed.emit(value);
        }
    }

    // declarations for control accessor
    onChange: any = () => { };
    onTouch: any = () => { };

    ngAfterViewInit(): void {
        this.origPlaceholder = this.placeholder;
        // checks if a label was provided and hides the label accordingly
        setTimeout(() => {
            this.hideLabel = !this.label ? true : false;
        });
    }

    writeValue(value: any): void {
        if (value !== this.innerValue) {
            this.value = value;
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouch = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    checkValidity() {
        // console.log('checkValidity', this.required, this.value);
        this.blurred.emit();
        if (this.required && !isNaN(parseFloat(this.value)) && !isNaN(this.value - 0) && (this.value <= 0 || !this.value)) {
            // checks if value is a number and if it's valid
            this.focusInvalidElement();
            this.checkingValidity.emit(this);
        } else if (this.required && typeof this.value === 'string' && Utilities.stringEmpty(this.value)) {
            this.focusInvalidElement();
            this.checkingValidity.emit(this);
        } else if (this.required && this.value?.length < this.minLength) {
            this.focusInvalidElement(`A minimum of ${this.minLength} characters is required.`);
            this.checkingValidity.emit(this);
        } else {
            this.isValid = true;
            this.blurredAndValid.emit();
            this.checkingValidity.emit(this);
        }
    }

    focusInvalidElement(message?: string) {
        this.isValid = false;
        const invalidControl: any = document.querySelector(`[id="${this.id}"]`);
        if (invalidControl && !invalidControl.classList.contains('invalid-el')) {
            invalidControl.classList.add('invalid-el');
            const errorElement = document.createElement('ion-text');
            if (this.errorDisplay === ErrorDisplay.LABEL) {
                errorElement.innerHTML = `${message ?? this.errorMessage}`;
                errorElement.className = 'danger';
                invalidControl.after(errorElement);
            } else {
                this.placeholder = 'Field required*';
            }
            invalidControl.addEventListener('ionChange', () => {
                errorElement.remove();
                this.placeholder = this.origPlaceholder ?? 'Enter information...';
                invalidControl.classList.remove('invalid-el');
                invalidControl.removeEventListener('ionChange', null);
            });
        }
    }

    showHelpMessage() {
        this.showHelpClick.emit();
    }


    protected updateValue() { }

    // functions for inputs
    private getRandomId() {
        let text = '';
        const possible = 'abcdefghijklmnopqrstuvwxyz1234679';
        for (let i = 0; i < 6; i++) {
            text += possible.charAt(Math.floor(Math.random() * possible.length));
        }
        return text;
    }

}

export function MakeProvider(type: any) {
    return {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => type),
        multi: true,
    };
}
